<template>
  <v-form ref="cutleryItemEditForm" v-model="isFormValid">
    <div class="back-btn" @click="navigateToIndex">
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M36 27H23.83L29.42 21.41L28 20L20 28L28 36L29.41 34.59L23.83 29H36V27Z" fill="#8C959D"/>
      </svg>
    </div>

    <v-row>
      <v-col cols="12">
        <h1>{{ pageTitle }}</h1>
      </v-col>

      <v-col cols="12" sm="5" md="4" xl="3">
        <v-tabs v-model="activeLanguageTab">
          <v-tab>RU</v-tab>
          <v-tab>UA</v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeLanguageTab">
          <v-tab-item :eager="true">
            <v-row>
              <v-col>
                <v-text-field
                    v-model="cutleryItem.name"
                    label="Название"
                    :rules="validationRules.required"
                    required
                    outlined
                />
              </v-col>
            </v-row>
          </v-tab-item>

          <v-tab-item :eager="true">
            <v-row>
              <v-col>
                <v-text-field
                    v-model="cutleryItem.name_ua"
                    label="Название"
                    :rules="validationRules.required"
                    required
                    outlined
                />
              </v-col>
            </v-row>
          </v-tab-item>
        </v-tabs-items>

        <v-text-field
            v-model="cutleryItem.price"
            :rules="validationRules.required"
            required
            label="Цена"
            suffix="UAH"
            outlined
        />

        <v-text-field
            v-model="cutleryItem.code"
            label="Артикул"
            outlined
        />

        <v-text-field
            v-model="cutleryItem.iiko_id"
            label="Iiko id блюда"
            outlined
        />

        <v-divider class="ma-4" />

        <v-btn
            :loading="isButtonLoading"
            :disabled="!isFormValid || isButtonLoading"
            color="primary"
            @click="handleSubmit"
        >
          {{ submitButtonText }}
        </v-btn>
      </v-col>
    </v-row>

    <v-snackbar
        v-model="notification.show"
        :timeout="notification.timeout"
        :color="notification.color"
        right
        top
    >
      {{ notification.text }}
      <template v-slot:action="{ attrs }">
        <v-btn
            v-bind="attrs"
            text
            @click="notification.show = false"
        >
          Закрыть
        </v-btn>
      </template>
    </v-snackbar>
  </v-form>
</template>

<script>
import CutleryItemService from './../../api/CutleryItemService'

const NOTIFICATION_TIMEOUT = 2000
const VALIDATION_MESSAGES = {
  REQUIRED: 'Нужно заполнить',
  REQUIRED_SELECT: 'Нужно заполнить'
}
const NOTIFICATION_MESSAGES = {
  UPDATE_SUCCESS: 'Успешно обновлено',
  UPDATE_ERROR: 'Ошибка при обновлении',
  CREATE_SUCCESS: 'Столовый прибор создан',
  CREATE_ERROR: 'Ошибка при создании'
}

export default {
  name: 'CutleryItemEdit',

  props: {
    id: {
      type: [String, Number],
      default: null,
    }
  },

  data() {
    return {
      isFormValid: true,
      isButtonLoading: false,
      activeLanguageTab: 0,

      notification: {
        show: false,
        text: '',
        color: ''
      },
      NOTIFICATION_TIMEOUT,

      validationRules: {
        required: [value => !!value || VALIDATION_MESSAGES.REQUIRED],
        requiredSelect: [v => !!v?.id || VALIDATION_MESSAGES.REQUIRED_SELECT],
      },

      cutleryItem: this.getInitialCutleryItem(),
    }
  },

  computed: {
    isEditMode() {
      return !!this.id
    },

    pageTitle() {
      return this.isEditMode
          ? 'Редактирование столового прибора'
          : 'Создание столового прибора'
    },

    submitButtonText() {
      return this.isEditMode ? 'Сохранить' : 'Создать'
    }
  },

  mounted() {
    if (this.isEditMode) {
      this.loadCutleryItem()
    }
  },

  methods: {
    getInitialCutleryItem() {
      return {
        name: '',
        name_ua: '',
        iiko_id: '',
        code: '',
        price: '0',
      }
    },

    async loadCutleryItem() {
      try {
        const response = await CutleryItemService.get(this.id)
        this.cutleryItem = response.data
      } catch (error) {
        this.showNotification(NOTIFICATION_MESSAGES.UPDATE_ERROR, 'error')
      }
    },

    navigateToIndex() {
      this.$router.push({ name: 'cutlery-item-index' })
    },

    validateForm() {
      this.$refs.cutleryItemEditForm.validate()
      this.switchToRequiredLanguageTab()
    },

    switchToRequiredLanguageTab() {
      const hasRussianName = !!this.cutleryItem.name
      const hasUkrainianName = !!this.cutleryItem.name_ua

      if (!hasRussianName && hasUkrainianName) {
        this.activeLanguageTab = 0
      } else if (hasRussianName && !hasUkrainianName) {
        this.activeLanguageTab = 1
      }
    },

    handleSubmit() {
      this.validateForm()

      if (!this.isFormValid) {
        return
      }

      if (this.isEditMode) {
        this.updateCutleryItem()
      } else {
        this.createCutleryItem()
      }
    },

    async updateCutleryItem() {
      this.isButtonLoading = true

      try {
        const formData = this.getFormData()
        await CutleryItemService.update(this.cutleryItem.id, formData)
        this.showNotification(NOTIFICATION_MESSAGES.UPDATE_SUCCESS, 'success')
      } catch (error) {
        this.showNotification(NOTIFICATION_MESSAGES.UPDATE_ERROR, 'error')
      } finally {
        this.isButtonLoading = false
      }
    },

    async createCutleryItem() {
      this.isButtonLoading = true

      try {
        const formData = this.getFormData()
        const response = await CutleryItemService.create(formData)

        this.cutleryItem.id = response.data.id
        this.showNotification(NOTIFICATION_MESSAGES.CREATE_SUCCESS, 'success')

        this.$router.push({
          name: 'cutlery-item-edit',
          params: { id: response.data.id }
        })
      } catch (error) {
        this.showNotification(NOTIFICATION_MESSAGES.CREATE_ERROR, 'error')
      } finally {
        this.isButtonLoading = false
      }
    },

    getFormData() {
      return {
        name: this.cutleryItem.name,
        name_ua: this.cutleryItem.name_ua,
        iiko_id: this.cutleryItem.iiko_id,
        code: this.cutleryItem.code,
        price: this.cutleryItem.price,
      }
    },

    showNotification(text, color) {
      this.notification = {
        show: true,
        text,
        color
      }
    }
  },
}
</script>

<style scoped lang="scss">
.back-btn {
  cursor: pointer;
  display: inline-block;
  margin-bottom: 16px;

  &:hover {
    opacity: 0.7;
  }
}

.mobile {
  width: 300px;
  height: 300px;
  margin-bottom: 24px;
}

.cutleryItem {
  position: relative;

  &:before {
    content: "";
    padding-top: 100%;
    display: block;
  }

  .content {
    position: absolute;
    width: 100%;
    left: 0;
    top: 0;
    height: 100%;
    background-color: #F8FBFF;
    border-right-style: 3px;

    .name {
      margin-top: 0.72vw;
      text-align: center;
      color: #8C959D;
    }
  }
}
</style>
